/**
 * Components for the company stage strategy
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Divider, Paper } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { COLORS } from '../../../config/colors';
import { COMPANY_STAGES, COMPANY_STAGE_CATEGORIES } from './constants';
import { setSelectedCompanyStages } from '../../../utils/api';
import centralDiscoveryQueue from '../../../utils/centralDiscoveryQueue';

/**
 * Read-only company stage confirmation component for embedding in chat messages
 * @param {Array} stages - Array of confirmed company stage names
 * @param {Object} stageData - The company stage data from API (optional, for percentages)
 * @returns {JSX.Element} The read-only company stage confirmation component
 */
export const CompanyStageConfirmationReadOnly = ({ stages = [], stageData = null }) => {
  // Find the stage keys from the stage names
  const stageKeys = stages.map(stageName => {
    const stageEntry = Object.entries(COMPANY_STAGES).find(([key, stage]) => stage.label === stageName);
    return stageEntry ? stageEntry[0] : null;
  }).filter(Boolean);

  // Group stages by category for display
  const stagesByCategory = Object.entries(COMPANY_STAGE_CATEGORIES).reduce((acc, [categoryKey, category]) => {
    const categoryStages = stageKeys
      .map(stageKey => {
        const stage = COMPANY_STAGES[stageKey];
        if (stage && stage.category === categoryKey) {
          return {
            key: stageKey,
            ...stage,
            count: stageData?.[stageKey] || 0
          };
        }
        return null;
      })
      .filter(Boolean);
    
    if (categoryStages.length > 0) {
      acc[categoryKey] = categoryStages;
    }
    return acc;
  }, {});

  // Calculate percentage for a stage
  const getStagePercentage = (stageKey) => {
    if (!stageData) return 0;
    const count = stageData[stageKey] || 0;
    const total = stageData.totalCount || 0;
    return total > 0 ? (count / total) * 100 : 0;
  };
  
  // Format percentage for display
  const formatPercentage = (percentage) => {
    if (percentage < 1 && percentage > 0) {
      return "<1%";
    } else {
      return `${Math.round(percentage)}%`;
    }
  };

  // Calculate the maximum percentage across selected stages for bar scaling
  const getMaxPercentage = () => {
    let maxPercentage = 0;
    
    stageKeys.forEach(stageKey => {
      const percentage = getStagePercentage(stageKey);
      if (percentage > maxPercentage) {
        maxPercentage = percentage;
      }
    });
    
    return Math.max(maxPercentage, 1);
  };

  if (stageKeys.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '600px' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          p: '20px',
          bgcolor: '#FFFFFF',
          width: '100%',
          mb: 2
        }}
      >
        {/* Render each category section that has selected stages */}
        {Object.entries(stagesByCategory).map(([categoryKey, stages], categoryIndex) => (
          <Box key={categoryKey} sx={{ mb: categoryIndex < Object.keys(stagesByCategory).length - 1 ? 3 : 2 }}>
            <Typography 
              variant='subtitle2' 
              sx={{ 
                fontWeight: 500, 
                color: '#374151', 
                mb: 1,
                fontSize: '14px'
              }}
            >
              {COMPANY_STAGE_CATEGORIES[categoryKey].label}
            </Typography>
            
            <Typography 
              variant='body2' 
              sx={{ 
                color: '#6B7280', 
                mb: 2,
                fontSize: '12px'
              }}
            >
              {categoryKey === 'CURRENT' 
                ? 'Employees currently working at these company stages' 
                : categoryKey === 'RECENT' 
                  ? 'Employees who recently worked at these company stages'
                  : 'Employees who previously worked at these company stages'}
            </Typography>
            
            {/* Company stage bars - only show selected stages */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: 2 }}>
              {stages.map((stage) => {
                const percentage = getStagePercentage(stage.key);
                const formattedPercentage = formatPercentage(percentage);
                const maxPercentage = getMaxPercentage();
                const barWidth = stageData ? (percentage / maxPercentage) * 100 : 60; // Default width if no data
                
                return (
                  <Box 
                    key={stage.key}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      height: '40px'
                    }}
                  >
                    {/* Bar - always show for selected stages */}
                    <Box
                      sx={{
                        width: `${Math.max(barWidth, 30)}%`, // Minimum width for visibility
                        height: '40px',
                        bgcolor: '#4A90E2',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        px: '12px',
                        minWidth: '120px',
                        position: 'relative'
                      }}
                    >
                      {/* Left side: Icon + Label */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircleIcon 
                          sx={{ 
                            fontSize: '20px', 
                            color: '#FFFFFF'
                          }} 
                        />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            color: '#FFFFFF'
                          }}
                        >
                          {stage.label}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Percentage - positioned at the right edge */}
                    {stageData && (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 2
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            color: '#374151'
                          }}
                        >
                          {formattedPercentage}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            
            {/* Add divider except for the last category */}
            {categoryIndex < Object.keys(stagesByCategory).length - 1 && (
              <Divider sx={{ my: 2 }} />
            )}
          </Box>
        ))}
      </Paper>

      {/* Confirmed label */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Typography
          sx={{
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            bgcolor: '#FFFFFF',
            color: '#969DAD',
            border: '1px solid #EAECF0'
          }}
        >
          Confirmed
        </Typography>
      </Box>

      {/* Restart Discovery (Company Stage) */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={async () => {
            try {
              await centralDiscoveryQueue.restartTopic('company_stage');
              if (typeof window !== 'undefined' && typeof window.goToIntakeView === 'function') {
                window.goToIntakeView();
              }
            } catch (e) {
              console.error('Failed to restart discovery for company_stage:', e);
            }
          }}
          sx={{
            textTransform: 'none',
            color: '#667085',
            '&:hover': { backgroundColor: 'rgba(70, 83, 102, 0.04)' }
          }}
        >
          Restart discovery
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Renders company stage options with multi-select capability matching Figma design
 * @param {Object} stageData - The company stage data from API
 * @param {Function} onStagesSelected - Callback when user selects stages
 * @param {Function} advanceToNextTopic - Function to advance to the next topic
 * @param {Function} setMessages - Function to update messages state
 * @param {Function} handleRefreshResults - Function to refresh match results
 * @param {Function} onSendText - Function to send text message
 * @param {Array} messages - Current conversation messages
 * @returns {JSX.Element} The company stage selection component
 */
export const CompanyStageOptions = ({
  stageData,
  onStagesSelected,
  advanceToNextTopic,
  setMessages,
  handleRefreshResults,
  onSendText,
  messages = []
}) => {
  // State for selected company stages
  const [selectedStages, setSelectedStages] = useState([]);
  // State to track if selection has been confirmed
  const [isConfirmed, setIsConfirmed] = useState(false);
  
  // Auto-select top 2 stages by default on initial render
  useEffect(() => {
    if (selectedStages.length === 0 && stageData) {
      // Flatten all stages from all categories
      const allStages = Object.entries(COMPANY_STAGES).map(([stageKey, stage]) => ({
        key: stageKey,
        ...stage,
        count: stageData?.[stageKey] || 0,
        percentage: getStagePercentage(stageKey)
      }));
      
      // Sort by percentage (highest first)
      const sortedStages = [...allStages].sort((a, b) => {
        const percentageA = getStagePercentage(a.key);
        const percentageB = getStagePercentage(b.key);
        return percentageB - percentageA;
      });
      
      // Select top 2 stages (if available)
      const topStages = sortedStages.slice(0, 2).map(stage => stage.key);
      
      // Update selected stages
      setSelectedStages(topStages);
    }
  }, [stageData]); // Only run when stageData changes

  // Group stages by category
  const stagesByCategory = Object.entries(COMPANY_STAGE_CATEGORIES).reduce((acc, [categoryKey, category]) => {
    acc[categoryKey] = Object.entries(COMPANY_STAGES)
      .filter(([stageKey, stage]) => stage.category === categoryKey)
      .map(([stageKey, stage]) => ({
        key: stageKey,
        ...stage,
        count: stageData?.[stageKey] || 0
      }));
    return acc;
  }, {});

  // Handle stage selection/deselection
  const handleStageToggle = (stageKey) => {
    if (isConfirmed) return; // Don't allow changes after confirmation
    
    setSelectedStages(prev => {
      if (prev.includes(stageKey)) {
        return prev.filter(key => key !== stageKey);
      } else {
        return [...prev, stageKey];
      }
    });
  };

  // Handle confirm button click
  const handleConfirm = () => {
    if (selectedStages.length === 0 || isConfirmed) {
      return; // Don't proceed if no stages selected or already confirmed
    }
    
    // Set confirmed state
    setIsConfirmed(true);
    
    // Get the selected stage details
    const selectedStageDetails = selectedStages.map(stageKey => {
      const stage = COMPANY_STAGES[stageKey];
      return {
        key: stageKey,
        ...stage
      };
    });
    
    // Store the selected stages in the global state for API functions to use
    setSelectedCompanyStages(selectedStageDetails);
    console.log('Stored selected company stages for API use:', selectedStageDetails);
    
    // Call the callback with the selected stages
    if (onStagesSelected) {
      onStagesSelected(selectedStageDetails.map(stage => stage.label));
    }
  };

  // Get the total count from stageData
  const getTotalCount = () => {
    return stageData.totalCount || 0;
  };
  
  // Calculate percentage for a stage
  const getStagePercentage = (stageKey) => {
    const count = stageData?.[stageKey] || 0;
    const total = getTotalCount();
    return total > 0 ? (count / total) * 100 : 0;
  };
  
  // Format percentage for display
  const formatPercentage = (percentage) => {
    if (percentage < 1 && percentage > 0) {
      return "<1%";
    } else {
      return `${Math.round(percentage)}%`;
    }
  };
  
  // Calculate the maximum percentage across all stages for bar scaling
  const getMaxPercentage = () => {
    let maxPercentage = 0;
    
    // Iterate through all stages in all categories
    Object.entries(stagesByCategory).forEach(([categoryKey, stages]) => {
      stages.forEach((stage) => {
        const percentage = getStagePercentage(stage.key);
        if (percentage > maxPercentage) {
          maxPercentage = percentage;
        }
      });
    });
    
    // Return the maximum percentage (minimum 1% to avoid division by zero)
    return Math.max(maxPercentage, 1);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '600px' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          p: '20px',
          bgcolor: '#FFFFFF',
          width: '100%',
          mb: 2
        }}
      >
        {/* Render each category section */}
        {Object.entries(stagesByCategory).map(([categoryKey, stages], categoryIndex) => (
          <Box key={categoryKey} sx={{ mb: categoryIndex < Object.keys(stagesByCategory).length - 1 ? 3 : 2 }}>
            <Typography 
              variant='subtitle2' 
              sx={{ 
                fontWeight: 500, 
                color: '#374151', 
                mb: 1,
                fontSize: '14px'
              }}
            >
              {COMPANY_STAGE_CATEGORIES[categoryKey].label}
            </Typography>
            
            <Typography 
              variant='body2' 
              sx={{ 
                color: '#6B7280', 
                mb: 2,
                fontSize: '12px'
              }}
            >
              {categoryKey === 'CURRENT' 
                ? 'Employees currently working at these company stages' 
                : categoryKey === 'RECENT' 
                  ? 'Employees who recently worked at these company stages'
                  : 'Employees who previously worked at these company stages'}
            </Typography>
            
            {/* Company stage bars */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: 2 }}>
              {stages.map((stage) => {
                const isSelected = selectedStages.includes(stage.key);
                const percentage = getStagePercentage(stage.key);
                const formattedPercentage = formatPercentage(percentage);
                const maxPercentage = getMaxPercentage();
                const barWidth = (percentage / maxPercentage) * 100; // Accurate percentage-based width
                
                return (
                  <Box 
                    key={stage.key}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      height: '40px',
                      cursor: isConfirmed ? 'default' : 'pointer',
                      opacity: isConfirmed && !isSelected ? 0.5 : 1,
                      transition: 'opacity 0.2s ease'
                    }}
                    onClick={() => handleStageToggle(stage.key)}
                  >
                    {/* Bar - only show if percentage > 0 */}
                    {percentage > 0 && (
                      <Box
                        sx={{
                          width: `${barWidth}%`,
                          height: '40px',
                          bgcolor: '#4A90E2',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          px: '12px',
                          minWidth: '120px', // Ensure minimum width for content
                          position: 'relative'
                        }}
                      >
                        {/* Left side: Icon + Label */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isSelected && (
                            <CheckCircleIcon 
                              sx={{ 
                                fontSize: '20px', 
                                color: '#FFFFFF'
                              }} 
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                              color: '#FFFFFF'
                            }}
                          >
                            {stage.label}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {/* For 0% stages, show just the label without a bar */}
                    {percentage === 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '40px',
                          px: '12px',
                          minWidth: '120px'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isSelected && (
                            <CheckCircleIcon 
                              sx={{ 
                                fontSize: '20px', 
                                color: '#4A90E2'
                              }} 
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                              color: '#374151'
                            }}
                          >
                            {stage.label}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Percentage - positioned at the right edge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '20px',
                          color: '#374151'
                        }}
                      >
                        {formattedPercentage}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            
            {/* Add divider except for the last category */}
            {categoryIndex < Object.keys(stagesByCategory).length - 1 && (
              <Divider sx={{ my: 2 }} />
            )}
          </Box>
        ))}
      </Paper>

      {/* Confirm Button - Outside the bordered box */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={handleConfirm}
          disabled={selectedStages.length === 0 || isConfirmed}
          sx={{
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            textTransform: 'none',
            minHeight: '40px',
            ...(selectedStages.length === 0 ? {
              // Disabled state - no selections
              bgcolor: '#BDD7F6',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: '#BDD7F6'
              }
            } : isConfirmed ? {
              // Confirmed state
              bgcolor: '#FFFFFF',
              color: '#969DAD',
              border: '1px solid #EAECF0',
              '&:hover': {
                bgcolor: '#FFFFFF'
              }
            } : {
              // Active state - has selections
              bgcolor: '#4A90E2',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: '#3A7BD8'
              }
            })
          }}
        >
          {selectedStages.length === 0 
            ? 'Select at Least One Option'
            : isConfirmed
              ? 'Confirmed'
              : 'Confirm Selection'}
        </Button>
      </Box>
    </Box>
  );
};
